import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

// ── Thunks — talk to your existing Oracle prefs API ──────────

export const loadUserPrefs = createAsyncThunk(
  "ui/loadUserPrefs",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().user?.token
      const { data } = await axios.post(
        "/api/users/getUserScreenPrefs",
        { screen_key: "GLOBAL" },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return data.prefs   // null if no prefs saved yet
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message)
    }
  }
)

export const saveUserPrefs = createAsyncThunk(
  "ui/saveUserPrefs",
  async (prefsToSave, { getState, rejectWithValue }) => {
    try {
      const token = getState().user?.token
      await axios.post(
        "/api/users/saveUserScreenPrefs",
        { screen_key: "GLOBAL", prefs: prefsToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return prefsToSave
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message)
    }
  }
)

// ── Per-screen prefs (column widths, sort, filters) ──────────
// ── Per-screen prefs (column widths, sort, filters) ──────────
export const loadScreenPrefs = createAsyncThunk(
  "ui/loadScreenPrefs",
  async (screenKey, { getState, rejectWithValue }) => {
    try {
      const token = getState().user?.token
      const { data } = await axios.post(
        "/api/users/getUserScreenPrefs",
        { screen_key: screenKey },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { screenKey, prefs: data.prefs }
    } catch (e) {
      return rejectWithValue(e.message)
    }
  },
  {
    // ── DEDUP GUARD ──────────────────────────────────────
    condition: (screenKey, { getState }) => {
      const state = getState().ui

      // Block #1: already loading this same screen? skip.
      if (state.screenPrefsLoading?.[screenKey]) return false

      // Block #2: recently loaded (< 5 seconds)? skip.
      const loadedAt = state.screenPrefsLoadedAt?.[screenKey]
      if (loadedAt && Date.now() - loadedAt < 5000) return false

      return true
    },
  }
)

export const saveScreenPrefs = createAsyncThunk(
  "ui/saveScreenPrefs",
  async ({ screenKey, prefs }, { getState, rejectWithValue }) => {
    try {
      const token = getState().user?.token
      await axios.post(
        "/api/users/saveUserScreenPrefs",
        { screen_key: screenKey, prefs },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      return { screenKey, prefs }
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

// ── Default preferences ──────────────────────────────────────
const DEFAULT_UI_PREFS = {
  theme:            "blue",    // blue|green|purple|amber|dark|rose
  font:             "ibm",     // ibm|dm|nunito
  density:          "normal",  // compact|normal|comfort
  sidebarCollapsed: false,
  showRecCount:     true,
  compactToolbar:   false,
  autosaveDrafts:   true,
  showFieldHints:   true,
}

const THEME_VARS = {
  blue:   { sb: "#042C53", sb2: "#0C447C", ac: "#185FA5", ac2: "#0C447C" },
  green:  { sb: "#052215", sb2: "#0A3D2B", ac: "#1D9E75", ac2: "#0F6E56" },
  purple: { sb: "#1A1640", sb2: "#26215C", ac: "#534AB7", ac2: "#3C3489" },
  amber:  { sb: "#1F1108", sb2: "#3D2110", ac: "#BA7517", ac2: "#854F0B" },
  dark:   { sb: "#0F0F12", sb2: "#18181B", ac: "#378ADD", ac2: "#185FA5" },
  rose:   { sb: "#1A0808", sb2: "#2D1010", ac: "#D85A30", ac2: "#993C1D" },
}

const FONT_MAP = {
  ibm:    "'IBM Plex Sans', system-ui, sans-serif",
  dm:     "'DM Sans', system-ui, sans-serif",
  nunito: "'Nunito', system-ui, sans-serif",
}

// Apply CSS variables to <html> so every component picks them up
const applyThemeToDom = (theme, font, density) => {
  const vars = THEME_VARS[theme] || THEME_VARS.blue
  const root = document.documentElement
  root.style.setProperty("--erp-sb",   vars.sb)
  root.style.setProperty("--erp-sb2",  vars.sb2)
  root.style.setProperty("--erp-ac",   vars.ac)
  root.style.setProperty("--erp-ac2",  vars.ac2)
  root.style.setProperty("--erp-font", FONT_MAP[font] || FONT_MAP.ibm)
  root.style.setProperty("--erp-row-h",
    density === "compact" ? "26px" :
    density === "comfort" ? "34px" : "30px"
  )
  root.style.setProperty("--erp-inp-h",
    density === "compact" ? "24px" :
    density === "comfort" ? "32px" : "28px"
  )
}

/*
const uiSlice = createSlice({
  name: "ui",
  initialState: {
    ...DEFAULT_UI_PREFS,
    syncStatus:  "idle",    // idle|loading|saving|synced|error
    syncError:   null,
    screenPrefs: {},        // { [screenKey]: { columns, sort, filters, ... } }
  },
  reducers: {
    // Instant local update → triggers debounced Oracle save in component
    setTheme: (state, { payload }) => {
      state.theme = payload
      applyThemeToDom(payload, state.font, state.density)
    },
    setFont: (state, { payload }) => {
      state.font = payload
      applyThemeToDom(state.theme, payload, state.density)
    },
    setDensity: (state, { payload }) => {
      state.density = payload
      applyThemeToDom(state.theme, state.font, payload)
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setSidebarCollapsed: (state, { payload }) => {
      state.sidebarCollapsed = payload
    },
    setUiPref: (state, { payload: { key, value } }) => {
      state[key] = value
    },
    // Per-screen column/sort/filter prefs
    setScreenPref: (state, { payload: { screenKey, key, value } }) => {
      if (!state.screenPrefs[screenKey]) state.screenPrefs[screenKey] = {}
      state.screenPrefs[screenKey][key] = value
    },
    setColumnVisible: (state, { payload: { screenKey, colId, visible } }) => {
      if (!state.screenPrefs[screenKey]) state.screenPrefs[screenKey] = {}
      if (!state.screenPrefs[screenKey].columns) state.screenPrefs[screenKey].columns = {}
      state.screenPrefs[screenKey].columns[colId] = visible
    },
    resetToDefaults: (state) => {
      Object.assign(state, DEFAULT_UI_PREFS)
      applyThemeToDom(DEFAULT_UI_PREFS.theme, DEFAULT_UI_PREFS.font, DEFAULT_UI_PREFS.density)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserPrefs.pending, (s) => { s.syncStatus = "loading" })
      .addCase(loadUserPrefs.fulfilled, (s, { payload }) => {
        s.syncStatus = "synced"
        if (!payload) return   // no prefs saved yet — use defaults
        const ui = payload.ui || {}
        Object.assign(s, { ...DEFAULT_UI_PREFS, ...ui })
        // Restore screen prefs
        if (payload.screens) s.screenPrefs = payload.screens
        // Apply to DOM immediately
        applyThemeToDom(s.theme, s.font, s.density)
      })
      .addCase(loadUserPrefs.rejected, (s, { payload }) => {
        s.syncStatus = "error"
        s.syncError  = payload
        // Fall back to defaults — app still works
        applyThemeToDom(s.theme, s.font, s.density)
      })
      .addCase(saveUserPrefs.pending,   (s) => { s.syncStatus = "saving" })
      .addCase(saveUserPrefs.fulfilled, (s) => { s.syncStatus = "synced" })
      .addCase(saveUserPrefs.rejected,  (s, { payload }) => {
        s.syncStatus = "error"
        s.syncError  = payload
      })
      .addCase(loadScreenPrefs.fulfilled, (s, { payload: { screenKey, prefs } }) => {
        if (prefs) s.screenPrefs[screenKey] = prefs
      })
      .addCase(saveScreenPrefs.fulfilled, (s, { payload: { screenKey, prefs } }) => {
        s.screenPrefs[screenKey] = prefs
      })
  },
})
*/
const uiSlice = createSlice({
  name: "ui",
  initialState: {
    ...DEFAULT_UI_PREFS,
    syncStatus:  "idle",    // idle|loading|saving|synced|error
    syncError:   null,
    screenPrefs: {},        // { [screenKey]: { columns, sort, filters, ... } }

    // ── NEW: dedup tracking for loadScreenPrefs ──────────────
    screenPrefsLoading:  {},   // { DASHBOARD: true } while fetch is in-flight
    screenPrefsLoadedAt: {},   // { DASHBOARD: 1712345678000 } unix ms
  },
  reducers: {
    // Instant local update → triggers debounced Oracle save in component
    setTheme: (state, { payload }) => {
      state.theme = payload
      applyThemeToDom(payload, state.font, state.density)
    },
    setFont: (state, { payload }) => {
      state.font = payload
      applyThemeToDom(state.theme, payload, state.density)
    },
    setDensity: (state, { payload }) => {
      state.density = payload
      applyThemeToDom(state.theme, state.font, payload)
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
    },
    setSidebarCollapsed: (state, { payload }) => {
      state.sidebarCollapsed = payload
    },
    setUiPref: (state, { payload: { key, value } }) => {
      state[key] = value
    },
    // Per-screen column/sort/filter prefs
    setScreenPref: (state, { payload: { screenKey, key, value } }) => {
      if (!state.screenPrefs[screenKey]) state.screenPrefs[screenKey] = {}
      state.screenPrefs[screenKey][key] = value
    },
    setColumnVisible: (state, { payload: { screenKey, colId, visible } }) => {
      if (!state.screenPrefs[screenKey]) state.screenPrefs[screenKey] = {}
      if (!state.screenPrefs[screenKey].columns) state.screenPrefs[screenKey].columns = {}
      state.screenPrefs[screenKey].columns[colId] = visible
    },
    resetToDefaults: (state) => {
      Object.assign(state, DEFAULT_UI_PREFS)
      applyThemeToDom(DEFAULT_UI_PREFS.theme, DEFAULT_UI_PREFS.font, DEFAULT_UI_PREFS.density)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserPrefs.pending, (s) => { s.syncStatus = "loading" })
      .addCase(loadUserPrefs.fulfilled, (s, { payload }) => {
        s.syncStatus = "synced"
        if (!payload) return   // no prefs saved yet — use defaults
        const ui = payload.ui || {}
        Object.assign(s, { ...DEFAULT_UI_PREFS, ...ui })
        // Restore screen prefs
        if (payload.screens) s.screenPrefs = payload.screens
        // Apply to DOM immediately
        applyThemeToDom(s.theme, s.font, s.density)
      })
      .addCase(loadUserPrefs.rejected, (s, { payload }) => {
        s.syncStatus = "error"
        s.syncError  = payload
        applyThemeToDom(s.theme, s.font, s.density)
      })
      .addCase(saveUserPrefs.pending,   (s) => { s.syncStatus = "saving" })
      .addCase(saveUserPrefs.fulfilled, (s) => { s.syncStatus = "synced" })
      .addCase(saveUserPrefs.rejected,  (s, { payload }) => {
        s.syncStatus = "error"
        s.syncError  = payload
      })

      // ── UPDATED: loadScreenPrefs with dedup tracking ─────────
      .addCase(loadScreenPrefs.pending, (s, { meta }) => {
        if (!s.screenPrefsLoading)  s.screenPrefsLoading  = {}   // ← guard
        s.screenPrefsLoading[meta.arg] = true
      })
      .addCase(loadScreenPrefs.fulfilled, (s, { payload: { screenKey, prefs } }) => {
        if (!s.screenPrefsLoading)  s.screenPrefsLoading  = {}   // ← guard
        if (!s.screenPrefsLoadedAt) s.screenPrefsLoadedAt = {}   // ← guard      
        if (prefs) s.screenPrefs[screenKey] = prefs
        s.screenPrefsLoading[screenKey]  = false
        s.screenPrefsLoadedAt[screenKey] = Date.now()
      })
      .addCase(loadScreenPrefs.rejected, (s, { meta }) => {
        if (!s.screenPrefsLoading) s.screenPrefsLoading = {}     // ← guard
        s.screenPrefsLoading[meta.arg] = false
        // Don't set loadedAt — so a retry won't be blocked by the 5s dedup window
      })

      .addCase(saveScreenPrefs.fulfilled, (s, { payload: { screenKey, prefs } }) => {
        s.screenPrefs[screenKey] = prefs
      })
  },
  condition: (screenKey, { getState }) => {
    const state = getState().ui
    if (state.screenPrefsLoading?.[screenKey])   return false   // ← optional chaining
    const loadedAt = state.screenPrefsLoadedAt?.[screenKey]     // ← optional chaining
    if (loadedAt && Date.now() - loadedAt < 5000) return false
    return true
  }
})

export const {
  setTheme, setFont, setDensity,
  toggleSidebar, setSidebarCollapsed,
  setUiPref, setScreenPref, setColumnVisible,
  resetToDefaults,
} = uiSlice.actions

// At bottom of file, near other selectors
export const selectScreenPrefsMeta = (screenKey) => (state) => ({
  loading:    state.ui.screenPrefsLoading?.[screenKey]  || false,
  loadedAt:   state.ui.screenPrefsLoadedAt?.[screenKey] || null,
  hasData:    Boolean(state.ui.screenPrefs?.[screenKey]),
})

export const selectUi           = (s) => s.ui
export const selectTheme        = (s) => s.ui.theme
export const selectDensity      = (s) => s.ui.density
export const selectFont         = (s) => s.ui.font
export const selectSidebarState = (s) => s.ui.sidebarCollapsed
export const selectScreenPrefs  = (screenKey) => (s) =>
  s.ui.screenPrefs[screenKey] || {}
export const selectSyncStatus   = (s) => s.ui.syncStatus

export default uiSlice.reducer